import type { RefundProtectQuoteRequest } from 'api/insurance'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { getCashRefundDeadline, getChangeOfMindBeforeCheckInDays, getFlexibleCancellationDays, isFlexibleCancellationPolicy, isWithinChangeOfMindPolicy, showCashRefundCancellation } from 'lib/offer/cancellationPolicyUtils'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import getObjectKey from 'lib/object/getObjectKey'
import { getCurrentIntl } from 'i18n/util/currentIntl'

interface CancellationPolicyInfo {
  cancellationType?: 'free' | 'flexible'
  cancelUntilFullRefundDate: moment.Moment
  cancelAfterNonRefundableDate?: moment.Moment
  cancellationAfterChangeOfMindPeriod: boolean
}

export function getBookingProtectionQuoteKey(quotePayload: RefundProtectQuoteRequest | null): string {
  if (!quotePayload) {
    return 'default'
  }

  return getObjectKey(quotePayload)
}

function showFreeCancellation(
  checkInDate?: string | moment.Moment,
  cancellationPolicy?: App.OfferCancellationPolicy,
) {
  if (!cancellationPolicy) {
    return false
  }
  if (cancellationPolicy.type !== 'post-purchase-seven-days') {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    return moment(checkInDate).subtract(21, 'days').isAfter(moment().startOf('day'))
  }
}

function showFlexibleCancellation(
  checkInDate?: string,
  cancellationPolicy?: App.OfferCancellationPolicy,
) {
  if (!cancellationPolicy) {
    return false
  }
  if (!isFlexibleCancellationPolicy(cancellationPolicy)) {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    const withinChangeOfMind = isWithinChangeOfMindPolicy(new Date(), checkInDate, cancellationPolicy.type)
    return withinChangeOfMind || moment(checkInDate).subtract(days, 'days').isAfter(moment().startOf('day'))
  }
}

export function isBookingProtectionAvailableForAccommodationOffer(offer: App.AnyOffer): boolean {
  if (offer.parentType === OFFER_TYPE_HOTEL) {
    if (offer.type === OFFER_TYPE_ALWAYS_ON) return true
    if (offer.type === OFFER_TYPE_HOTEL) return true
    if (offer.type === OFFER_TYPE_BED_BANK) return true
  }

  return false
}

export function getCancellationPolicyInfo(
  checkInDate?: string,
  cancellationPolicy?: App.OfferCancellationPolicy,
): CancellationPolicyInfo {
  const freeCancellation = showFreeCancellation(checkInDate, cancellationPolicy)
  const cashRefund = showCashRefundCancellation(checkInDate, false, cancellationPolicy)
  const flexibleCancellation = showFlexibleCancellation(checkInDate, cancellationPolicy)
  const cancellationType = freeCancellation || cashRefund ? 'free' : flexibleCancellation ? 'flexible' : undefined
  let startBasedOnToday: moment.Moment = moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let cancelUntilFullRefundDate: moment.Moment = moment()
  let cancelAfterNonRefundableDate: moment.Moment | undefined
  let cancellationAfterChangeOfMindPeriod = false

  if (freeCancellation) {
    const startBasedOnCheckIn = checkInDate && moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
    if (startBasedOnCheckIn) {
      cancelUntilFullRefundDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
    }
  } else if (cashRefund && checkInDate) {
    cancelUntilFullRefundDate = getCashRefundDeadline(checkInDate) ?? cancelUntilFullRefundDate
  } else if (flexibleCancellation && cancellationPolicy && checkInDate) {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    let startBasedOnCheckIn = moment(checkInDate).subtract(days, 'days')

    const changeOfMindDeadline = moment(checkInDate).subtract(getChangeOfMindBeforeCheckInDays(cancellationPolicy.type), 'days')
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

    cancellationAfterChangeOfMindPeriod = startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
    startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

    cancelUntilFullRefundDate = startBasedOnToday
    cancelAfterNonRefundableDate = startBasedOnCheckIn
  }

  return {
    cancellationType,
    cancelUntilFullRefundDate,
    cancelAfterNonRefundableDate,
    cancellationAfterChangeOfMindPeriod,
  }
}

export function getBookingProtectionCancellationReasons(regionCode?: string) {
  const intl = getCurrentIntl()
  const shouldHideGbUnsupportedReasons = regionCode === 'GB'

  return [
    intl.formatMessage({ id: 'a2Z9Qd9jy4aIgcqmlbHb', defaultMessage: 'Illness/injury (including Covid)', description: 'Cancellation reason option for illness or injury in booking protection' }),
    intl.formatMessage({ id: 'ABBKcwS9_p8C04FbRWC5', defaultMessage: 'Home emergency', description: 'Cancellation reason option for home emergency in booking protection' }),
    intl.formatMessage({ id: 'e_6IrUm6CwqEKuTiG2Yl', defaultMessage: 'Pre-existing medical condition', description: 'Cancellation reason option for pre-existing medical condition in booking protection' }),
    ...(!shouldHideGbUnsupportedReasons ? [intl.formatMessage({ id: 'VQ5X7PvFEdjrAoe_LaSL', defaultMessage: 'Flight disruption', description: 'Cancellation reason option for flight disruption in booking protection' })] : []),
    intl.formatMessage({ id: 'H7T2V9pbXmgUotXSVnHb', defaultMessage: 'Adverse weather', description: 'Cancellation reason option for adverse weather in booking protection' }),
    intl.formatMessage({ id: '74gUdjHg-GDN6G-wmw4w', defaultMessage: 'Theft of documents', description: 'Cancellation reason option for theft of documents in booking protection' }),
    intl.formatMessage({ id: 'dWMPjWGAaopgB1g767Af', defaultMessage: 'Workplace redundancy', description: 'Cancellation reason option for workplace redundancy in booking protection' }),
    intl.formatMessage({ id: 'NzGEDY9PV73qgitm1eLx', defaultMessage: 'Jury service', description: 'Cancellation reason option for jury service in booking protection' }),
    intl.formatMessage({ id: 'g7BwWhak8cv0aoDUN7Hd', defaultMessage: 'Court summons', description: 'Cancellation reason option for court summons in booking protection' }),
    intl.formatMessage({ id: 'XHlDYFOdoZhVHRyWt3mi', defaultMessage: 'Pregnancy complications', description: 'Cancellation reason option for pregnancy complications in booking protection' }),
    intl.formatMessage({ id: 'Czvdfz5C_GEchjJktX0n', defaultMessage: 'Death of immediate family member', description: 'Cancellation reason option for death of family member in booking protection' }),
    intl.formatMessage({ id: 'N-yRj7jkpwsgwjweRvtL', defaultMessage: 'Mechanical breakdown', description: 'Cancellation reason option for mechanical breakdown in booking protection' }),
    intl.formatMessage({ id: 'aiajSR0MdBc9qKXFp4RC', defaultMessage: 'Relocated for work', description: 'Cancellation reason option for work relocation in booking protection' }),
    intl.formatMessage({ id: 'Y9Z1o-izSN8mkOVmO6Fs', defaultMessage: 'Armed forces & emergency services recall', description: 'Cancellation reason option for armed forces recall in booking protection' }),
    ...(!shouldHideGbUnsupportedReasons ? [intl.formatMessage({ id: 'K1eTr26z83u_K4eGiKm7', defaultMessage: 'Changes to examination dates', description: 'Cancellation reason option for exam date changes in booking protection' })] : []),
  ]
}

export function getFlightsBookingProtectionCancellationReasons() {
  const intl = getCurrentIntl()
  return [
    intl.formatMessage({ id: 'a2Z9Qd9jy4aIgcqmlbHb', defaultMessage: 'Illness/injury (including Covid)', description: 'Cancellation reason option for illness or injury in booking protection' }),
    intl.formatMessage({ id: 'e_6IrUm6CwqEKuTiG2Yl', defaultMessage: 'Pre-existing medical condition', description: 'Cancellation reason option for pre-existing medical condition in booking protection' }),
    intl.formatMessage({ id: 'g7BwWhak8cv0aoDUN7Hd', defaultMessage: 'Court summons', description: 'Cancellation reason option for court summons in booking protection' }),
    intl.formatMessage({ id: 'dWMPjWGAaopgB1g767Af', defaultMessage: 'Workplace redundancy', description: 'Cancellation reason option for workplace redundancy in booking protection' }),
    intl.formatMessage({ id: 'Czvdfz5C_GEchjJktX0n', defaultMessage: 'Death of immediate family member', description: 'Cancellation reason option for death of family member in booking protection' }),
    intl.formatMessage({ id: 'gw4iOB6T5yyR3NY1gT1D', defaultMessage: 'Public transport failure', description: 'Cancellation reason option for public transport failure in booking protection' }),
    intl.formatMessage({ id: 'aiajSR0MdBc9qKXFp4RC', defaultMessage: 'Relocated for work', description: 'Cancellation reason option for work relocation in booking protection' }),
    intl.formatMessage({ id: 'Y9Z1o-izSN8mkOVmO6Fs', defaultMessage: 'Armed forces & emergency services recall', description: 'Cancellation reason option for armed forces recall in booking protection' }),
    intl.formatMessage({ id: 'ABBKcwS9_p8C04FbRWC5', defaultMessage: 'Home emergency', description: 'Cancellation reason option for home emergency in booking protection' }),
    intl.formatMessage({ id: '74gUdjHg-GDN6G-wmw4w', defaultMessage: 'Theft of documents', description: 'Cancellation reason option for theft of documents in booking protection' }),
    intl.formatMessage({ id: 'XHlDYFOdoZhVHRyWt3mi', defaultMessage: 'Pregnancy complications', description: 'Cancellation reason option for pregnancy complications in booking protection' }),
    intl.formatMessage({ id: 'NzGEDY9PV73qgitm1eLx', defaultMessage: 'Jury service', description: 'Cancellation reason option for jury service in booking protection' }),
    intl.formatMessage({ id: 'VQ5X7PvFEdjrAoe_LaSL', defaultMessage: 'Flight disruption', description: 'Cancellation reason option for flight disruption in booking protection' }),
    intl.formatMessage({ id: 'N-yRj7jkpwsgwjweRvtL', defaultMessage: 'Mechanical breakdown', description: 'Cancellation reason option for mechanical breakdown in booking protection' }),
    intl.formatMessage({ id: 'H7T2V9pbXmgUotXSVnHb', defaultMessage: 'Adverse weather', description: 'Cancellation reason option for adverse weather in booking protection' }),
    intl.formatMessage({ id: 'K1eTr26z83u_K4eGiKm7', defaultMessage: 'Changes to examination dates', description: 'Cancellation reason option for exam date changes in booking protection' }),
  ]
}
